import { useId, useState } from 'react';
import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import { Box, Button, CircularProgress, Popover, Stack, Typography } from '@mui/material';

import type { OpenRequirementItem } from '../store/useOpenRequirements';
import type { SubjectLanguage } from '../types';

export interface RequirementAlertBadgeProps {
  items: OpenRequirementItem[];
  /** True when at least one item could not be named — drives count-only copy. */
  hasUnnamedItems: boolean;
  subject: SubjectLanguage;
  /** Absent when the provider cannot send, e.g. no contact details on file. */
  onRemind?: () => void;
  isReminding?: boolean;
  /** Result of the last send, so the popover can confirm without a toast. */
  remindOutcome?: 'sent' | 'failed';
  onOpenPatient?: () => void;
  onOpen?: () => void;
}

/** Hollow dot: something is still owed. Filled would mean settled. */
const OwedDot = () => (
  <Box
    component="span"
    aria-hidden
    sx={{
      width: 10,
      height: 10,
      borderRadius: '50%',
      flexShrink: 0,
      border: `2px solid ${ELIG_TOKENS.red}`,
      boxSizing: 'border-box',
    }}
  />
);

/**
 * The provider alert in the recording window's patient context bar (AC-15).
 *
 * Q6 is open. This follows the standing recommendation and the reviewed round-1
 * prototype: a context-bar badge opening a compact checklist, with NO permanent
 * dismissal while items stay open — closing the popover hides the list, never
 * the badge. Financial detail stays out pending Q3.
 */
export const RequirementAlertBadge = ({
  items,
  hasUnnamedItems,
  subject,
  onRemind,
  isReminding = false,
  remindOutcome,
  onOpenPatient,
  onOpen,
}: RequirementAlertBadgeProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const popoverId = useId();

  // Nothing outstanding is not an empty alert — it is no alert.
  if (items.length === 0) return null;

  const open = Boolean(anchorEl);
  const [first] = items;
  // One named item gets its own words — this is what makes AC-15's "Card
  // missing" read the way it does. Anything else falls back to a count, so the
  // badge never grows unbounded or shows an identifier.
  const canNameFirst = Boolean(first.label) && !hasUnnamedItems;
  const primaryText = canNameFirst
    ? `${first.label} missing`
    : `${items.length} item${items.length === 1 ? '' : 's'} needed`;
  const overflow = canNameFirst && items.length > 1 ? items.length - 1 : 0;

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={event => {
          setAnchorEl(event.currentTarget);
          onOpen?.();
        }}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.25,
          py: 0.5,
          borderRadius: '999px',
          border: `1px solid ${ELIG_TOKENS.red}`,
          backgroundColor: ELIG_TOKENS.redTint,
          cursor: 'pointer',
          font: 'inherit',
          '&:hover': { backgroundColor: ELIG_TOKENS.redSoft },
        }}
      >
        <OwedDot />
        <Typography
          component="span"
          sx={{ fontSize: 12.5, fontWeight: 700, color: ELIG_TOKENS.red }}
        >
          {primaryText}
        </Typography>
        {overflow > 0 && (
          <Typography
            component="span"
            sx={{ fontSize: 12, color: ELIG_TOKENS.inkSoft }}
          >
            +{overflow} item{overflow === 1 ? '' : 's'}
          </Typography>
        )}
      </Box>

      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 2, width: 340 } } }}
      >
        <Stack spacing={1.25}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: ELIG_TOKENS.ink }}>
            {items.length} {subject.singular} item{items.length === 1 ? '' : 's'} still
            missing
          </Typography>

          <Typography sx={{ fontSize: 12.5, color: ELIG_TOKENS.inkSoft, lineHeight: 1.4 }}>
            Worth a mention while the {subject.singular} is in the room — most finish the
            secure link in about two minutes.
          </Typography>

          <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {items.map(item => (
              <Box
                component="li"
                key={item.patientRequirementId}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}
              >
                <OwedDot />
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: ELIG_TOKENS.ink }}>
                  {item.label ?? `An item your practice needs`}
                </Typography>
              </Box>
            ))}
          </Box>

          {remindOutcome === 'sent' && (
            <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: ELIG_TOKENS.green }}>
              Sent. The badge clears once they finish.
            </Typography>
          )}
          {remindOutcome === 'failed' && (
            <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: ELIG_TOKENS.red }}>
              We could not send that. Try from the {subject.singular} record.
            </Typography>
          )}

          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            {onRemind && (
              <Button
                size="small"
                variant="contained"
                onClick={onRemind}
                disabled={isReminding}
                startIcon={
                  isReminding ? <CircularProgress size={12} color="inherit" /> : undefined
                }
                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
              >
                Remind {subject.singular}
              </Button>
            )}
            {onOpenPatient && (
              <Button
                size="small"
                variant="text"
                onClick={onOpenPatient}
                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
              >
                Open record
              </Button>
            )}
          </Stack>

          <Typography sx={{ fontSize: 12, color: ELIG_TOKENS.inkFaint, lineHeight: 1.4 }}>
            This clears on its own when evidence arrives. It cannot be dismissed while
            items stay open.
          </Typography>
        </Stack>
      </Popover>
    </>
  );
};
